import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDZ } from '../../../utils/currency';

const OrdersTable = () => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  const orders = [
    {
      id: 'ORD-2024-001',
      customer: 'John Smith',
      email: 'john.smith@email.com',
      products: 3,
      total: 1299.99,
      status: 'pending',
      date: '2024-09-23',
      time: '14:30',
      paymentMethod: 'Credit Card',
      shippingAddress: '123 Tech Street, San Francisco, CA 94105'
    },
    {
      id: 'ORD-2024-002',
      customer: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      products: 1,
      total: 899.99,
      status: 'processing',
      date: '2024-09-23',
      time: '13:15',
      paymentMethod: 'PayPal',
      shippingAddress: '456 Innovation Ave, Austin, TX 78701'
    },
    {
      id: 'ORD-2024-003',
      customer: 'Michael Chen',
      email: 'm.chen@email.com',
      products: 2,
      total: 549.98,
      status: 'shipped',
      date: '2024-09-22',
      time: '16:45',
      paymentMethod: 'Credit Card',
      shippingAddress: '789 Digital Blvd, Seattle, WA 98101'
    },
    {
      id: 'ORD-2024-004',
      customer: 'Emily Davis',
      email: 'emily.davis@email.com',
      products: 4,
      total: 2199.96,
      status: 'delivered',
      date: '2024-09-21',
      time: '11:20',
      paymentMethod: 'Apple Pay',
      shippingAddress: '321 Future Lane, New York, NY 10001'
    },
    {
      id: 'ORD-2024-005',
      customer: 'David Wilson',
      email: 'd.wilson@email.com',
      products: 1,
      total: 199.99,
      status: 'cancelled',
      date: '2024-09-20',
      time: '09:30',
      paymentMethod: 'Credit Card',
      shippingAddress: '654 Tech Park, Boston, MA 02101'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/20 text-warning border-warning/30',
      processing: 'bg-primary/20 text-primary border-primary/30',
      shipped: 'bg-secondary/20 text-secondary border-secondary/30',
      delivered: 'bg-success/20 text-success border-success/30',
      cancelled: 'bg-error/20 text-error border-error/30'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      processing: 'Package',
      shipped: 'Truck',
      delivered: 'CheckCircle',
      cancelled: 'XCircle'
    };
    return icons?.[status] || 'Clock';
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders?.filter(order => order?.status === statusFilter);

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev?.includes(orderId) 
        ? prev?.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    setSelectedOrders(
      selectedOrders?.length === filteredOrders?.length 
        ? [] 
        : filteredOrders?.map(order => order?.id)
    );
  };

  return (
    <div className="holographic-card rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-foreground">Recent Orders</h3>
          <div className="flex items-center space-x-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="px-3 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <Button variant="outline" iconName="Download" iconPosition="left">
              Export
            </Button>
          </div>
        </div>

        {selectedOrders?.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-primary/10 border border-primary/30 rounded-lg">
            <span className="text-sm text-primary font-medium">
              {selectedOrders?.length} order(s) selected
            </span>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Mail">
                Send Email
              </Button>
              <Button variant="outline" size="sm" iconName="Package">
                Update Status
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              <th className="p-4 text-left">
                <input
                  type="checkbox"
                  checked={selectedOrders?.length === filteredOrders?.length && filteredOrders?.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary/50"
                />
              </th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Order ID</th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Customer</th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Products</th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Total</th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Date</th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders?.map((order) => (
              <tr key={order?.id} className="border-b border-border hover:bg-muted/10 transition-colors">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedOrders?.includes(order?.id)}
                    onChange={() => handleSelectOrder(order?.id)}
                    className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary/50"
                  />
                </td>
                <td className="p-4">
                  <span className="font-mono text-sm text-primary">{order?.id}</span>
                </td>
                <td className="p-4">
                  <div>
                    <p className="font-medium text-foreground">{order?.customer}</p>
                    <p className="text-sm text-muted-foreground">{order?.email}</p>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{order?.products} items</span>
                </td>
                <td className="p-4">
                  <span className="font-semibold text-foreground">{formatDZ(order?.total)}</span>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order?.status)}`}>
                    <Icon name={getStatusIcon(order?.status)} size={12} />
                    <span className="capitalize">{order?.status}</span>
                  </span>
                </td>
                <td className="p-4">
                  <div>
                    <p className="text-sm text-foreground">{order?.date}</p>
                    <p className="text-xs text-muted-foreground">{order?.time}</p>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm" iconName="Eye">
                      View
                    </Button>
                    <Button variant="ghost" size="sm" iconName="Edit">
                      Edit
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredOrders?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No orders found</h3>
          <p className="text-muted-foreground">No orders match the selected filter criteria.</p>
        </div>
      )}
    </div>
  );
};

export default OrdersTable;